import type { PaletteToken } from '../shared/palette';
import { TICKS_PER_BEAT } from '../shared/tempo';

const SMILE_TICKS = TICKS_PER_BEAT;
const CALM_FADE_TICKS = TICKS_PER_BEAT;

export interface Farewell<V> {
  readonly view: V;
  live: boolean;
  id: number;
  start: number;
  x: number;
  y: number;
  radius: number;
  tilt: number;
  token: PaletteToken;
}

export type FarewellSpec = Omit<Farewell<never>, 'view' | 'live'>;

export interface FarewellHooks<V> {
  show(farewell: Farewell<V>, smile: number, fade: number): void;
  hide(view: V): void;
  burst(farewell: Farewell<V>): void;
}

// The sim has already dropped it: this queue is all that is left of it on screen.
export class Farewells<V> {
  private readonly items: Farewell<V>[];
  private next = 0;

  constructor(capacity: number, create: () => V) {
    this.items = Array.from({ length: capacity }, () => ({
      view: create(),
      live: false,
      id: 0,
      start: 0,
      x: 0,
      y: 0,
      radius: 0,
      tilt: 0,
      token: 'or',
    }));
  }

  get liveCount(): number {
    return this.items.filter((item) => item.live).length;
  }

  start(spec: FarewellSpec): void {
    const slot = this.items[this.next];
    if (slot === undefined) {
      return;
    }
    this.next = (this.next + 1) % this.items.length;
    Object.assign(slot, spec, { live: true });
  }

  // A bad vibe recruited as it fell lives on as a recruit: no smile, no burst.
  cancel(id: number, hide: (view: V) => void): void {
    for (const item of this.items) {
      if (item.live && item.id === id) {
        item.live = false;
        hide(item.view);
      }
    }
  }

  update(now: number, calm: boolean, hooks: FarewellHooks<V>): void {
    for (const item of this.items) {
      if (!item.live) {
        continue;
      }
      const elapsed = now - item.start;
      if (elapsed < 0) {
        item.live = false;
        hooks.hide(item.view);
      } else if (elapsed < SMILE_TICKS) {
        hooks.show(item, elapsed / SMILE_TICKS, 0);
      } else if (calm && elapsed < SMILE_TICKS + CALM_FADE_TICKS) {
        hooks.show(item, 1, (elapsed - SMILE_TICKS) / CALM_FADE_TICKS);
      } else {
        item.live = false;
        hooks.hide(item.view);
        if (!calm) {
          hooks.burst(item);
        }
      }
    }
  }

  clear(hide: (view: V) => void): void {
    for (const item of this.items) {
      item.live = false;
      hide(item.view);
    }
  }
}
